import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, forwardRef, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as moment from 'moment';

@Component({
  selector: 'oops-gem-timepicker',
  templateUrl: './oops-gem-timepicker.component.html',
  styleUrls: ['./oops-gem-timepicker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OopsGemTimePickerComponent),
      multi: true
    }
  ]
})
export class OopsGemTimePickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  private outputDateFormat: string = 'YYYY-MM-DD HH:mm:ss';
  private outputTimeFormat: string = 'HH:mm:ss';
  @Input() displayFormat: string = 'HH:mm';
  @Input() autoApply: boolean = true;
  @Input() disabled: boolean = false;
  @Input() outputTimeOnly: boolean = true;

  private _data: any = null;
  @Input()
  get data(): any {
    return this._data;
  }
  set data(value: any) {
    if (value !== this._data) {
      this._data = value;
      this.parseValueToTimeParts(this._data);
      this.updateDisplayValue();
    }
  }
  @Output() dataChange: EventEmitter<string> = new EventEmitter<string>();

  public _showError: boolean = false;
  @Input("errorMessage") errorMessage: string;
  @Input("showError")
  set showError(value: boolean) {
    this._showError = value;
  }
  @Output() showErrorChange: EventEmitter<boolean> = new EventEmitter();

  @ViewChild('displayBox') displayBox!: ElementRef;
  @ViewChild('pickerPopup') pickerPopup!: ElementRef;

  selectedHour: number = 0;
  selectedMinute: number = 0;
  selectedMeridiem: 'AM' | 'PM' = 'AM';

  displayHourOptions: number[] = [];
  displayMinuteOptions: number[] = Array.from({ length: 60 }, (_, i) => i);

  showMeridiem: boolean = false;
  private inputMode: '24h' | '12h' = '24h';

  private _value: any = null;
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  showPicker: boolean = false;
  displayValue: string = '';

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
    this.updateTimeOptions();
    if (this._data !== null) {
      this.parseValueToTimeParts(this._data);
    } else if (this._value !== null && this._value !== undefined && this._value !== '') {
      this.parseValueToTimeParts(this._value);
    }
    this.updateDisplayValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayFormat']) {
      this.updateTimeOptions();
      this.updateDisplayValue();
    }
    if (changes['data'] && !changes['data'].firstChange) {
      if (this._data === null || this._data === undefined || this._data === '') {
        this.clearTimeParts();
      } else {
        this.parseValueToTimeParts(this._data);
      }
      this.updateDisplayValue();
    }
    if (changes['autoApply'] && this.autoApply) {
      this.applyChanges();
    }
    if (changes['disabled']) {
      if (this.disabled) {
        this.showPicker = false;
      }
    }
  }

  // --- ControlValueAccessor implementation ---
  writeValue(value: any): void {
    if (this._data === null) {
      if (value === null || value === undefined || value === '') {
        this.clearTimeParts();
      } else {
        this._value = value;
        this.parseValueToTimeParts(this._value);
      }
      this.updateDisplayValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.disabled) {
      this.showPicker = false;
    }
  }
  // --- End ControlValueAccessor implementation ---

  /**
   * Parses the input value into selectedHour, selectedMinute, selectedMeridiem
   * @param value The value to parse (could be from ngModel or [(data)])
   */
  private parseValueToTimeParts(value: any): void {
      let timeMoment: moment.Moment | null = null;

      if (moment.isMoment(value)) {
          timeMoment = value;
      } else if (value instanceof Date) {
          timeMoment = moment(value);
      } else if (typeof value === 'string') {
        timeMoment = moment(value, 'YYYY-MM-DD', true);
        if (!timeMoment.isValid()) timeMoment = moment(value, 'YYYY-MM-DD HH:mm:ss', true);
        if (!timeMoment.isValid()) timeMoment = moment(value, 'HH:mm', true);
        if (!timeMoment.isValid()) {
           timeMoment = moment(value, 'HH:mm:ss', true);
        }
      }

      if (timeMoment) {
          this.selectedMinute = timeMoment.minute();

          if (this.inputMode === '12h') {
              this.selectedMeridiem = timeMoment.hour() >= 12 ? 'PM' : 'AM';
              let hour = timeMoment.hour();
              if (hour === 0) {
                  this.selectedHour = 12;
              } else if (hour > 12) {
                  this.selectedHour = hour - 12;
              } else {
                  this.selectedHour = hour;
              }
          } else {
              this.selectedHour = timeMoment.hour();
          }
      } else {
          this.selectedHour = 0;
          this.selectedMinute = 0;
          this.selectedMeridiem = 'AM';
      }
  }

  private clearTimeParts(): void {
    this.selectedHour = 0;
    this.selectedMinute = 0;
    this.selectedMeridiem = 'AM';
  }

  private updateTimeOptions(): void {
    this.showMeridiem = this.displayFormat.includes('h');
    this.inputMode = this.showMeridiem ? '12h' : '24h';

    this.displayHourOptions = [];
    if (this.inputMode === '12h') {
      for (let i = 1; i <= 12; i++) {
        this.displayHourOptions.push(i);
      }
    } else {
      for (let i = 0; i < 24; i++) {
        this.displayHourOptions.push(i);
      }
    }
  }

  private updateDisplayValue(): void {
    const currentBoundValue = (this._data !== null && this._data !== undefined) ? this._data : this._value;

    if (currentBoundValue === null || currentBoundValue === undefined || currentBoundValue === '') {
      this.displayValue = '';
    } else {
      let hourForDisplay = this.selectedHour;
      if (this.inputMode === '12h') {
          if (this.selectedMeridiem === 'PM' && hourForDisplay !== 12) {
              hourForDisplay += 12;
          } else if (this.selectedMeridiem === 'AM' && hourForDisplay === 12) {
              hourForDisplay = 0;
          }
      }
      const tempMoment = moment().hour(hourForDisplay).minute(this.selectedMinute).second(0).millisecond(0);
      this.displayValue = tempMoment.format(this.displayFormat);
    }
  }

  /**
   * Emits the selected time in ISO datetime format.
   */
  private emitValue(valueToEmit: string | null = null): void {
    let outputString: string;
    if (valueToEmit === null) {
      let hourToSet = this.selectedHour;
      if (this.inputMode === '12h') {
          if (this.selectedMeridiem === 'PM' && hourToSet !== 12) {
              hourToSet += 12;
          } else if (this.selectedMeridiem === 'AM' && hourToSet === 12) {
              hourToSet = 0;
          }
      }
      const now = moment();
      const outputMoment = now.hour(hourToSet).minute(this.selectedMinute).second(0).millisecond(0);
      outputString = outputMoment.format(this.getOutputFormat());
    } else {
      outputString = valueToEmit;
    }

    this.onChange(outputString);
    this.onTouched();

    this.dataChange.emit(outputString);
    this._data = outputString;

    this.updateDisplayValue();
  }

  private getOutputFormat(): string {
    return this.outputTimeOnly ? this.outputTimeFormat : this.outputDateFormat;
  }

  onTimeChange(): void {
    if (this.autoApply) {
      this.applyChanges();
    }
  }

  applyChanges(): void {
    if (this.disabled) return;
    this.emitValue();
    this.showPicker = false;
  }

  cancelChanges(): void {
    if (this.disabled) return;
    const originalValue = (this._data !== null && this._data !== undefined) ? this._data : this._value;
    if (originalValue === null || originalValue === undefined || originalValue === '') {
      this.clearTimeParts();
    } else {
      this.parseValueToTimeParts(originalValue);
    }
    this.updateDisplayValue();
    this.showPicker = false;
  }

  togglePicker(): void {
    if (this.disabled) {
      this.showPicker = false;
      return;
    }
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      const currentValue = (this._data !== null && this._data !== undefined) ? this._data : this._value;
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        this.clearTimeParts();
      } else {
        this.parseValueToTimeParts(currentValue);
      }
      this.updateDisplayValue();
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: Event): void {
    if (this.disabled) return;

    if (this.showPicker && !this.elementRef.nativeElement.contains(event.target)) {
      const target = event.target as HTMLElement;
      if (target.classList.contains('clear-button') || target.closest('.clear-button')) {
        return;
      }

      if (this.autoApply) {
        this.applyChanges();
      } else {
        this.cancelChanges();
      }
      this.showPicker = false;
    }
  }

  public getPlaceHolder(): string {
    return this.displayFormat.toLowerCase();
  }

  clearTime(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;

    this.clearTimeParts(); 
    this.emitValue('');
    this.showPicker = false;
    
  }

  get hasValue(): boolean {
    return this.displayValue !== '';
  }
}